//DisplayCardPage.js
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import guestApi from '../utils/guestApi';
import '../styles/DisplayCardPage.css';
import { useToast } from '../contexts/ToastContext';
import { shareOrDownloadVCard } from '../utils/vCard';
import {
  trackPublicNfcCardViewed,
  trackPublicDigitalCardViewed,
  trackAddToContactsClick,
  trackAddToContactsSuccess,
  trackGuestSaveModalOpen,
  trackGuestEmailSubmitted,
  trackGuestImmediateSaveSuccess,
  trackGuestFollowupEmailSent,
} from '../utils/guestFunnelAnalytics';
import {
  enqueueGuestSave,
  flushGuestSaveQueue,
  shouldEnqueueGuestSaveFromRequestError,
} from '../utils/guestOfflineQueue';
import { activateCard, activationErrorMessage, isAlreadyHasCardError } from '../utils/cardActivation';
import CardActivationPanel from '../components/CardActivationPanel';
import VeloxCard from '../components/card/VeloxCard';
import useDeferredLoading from '../hooks/useDeferredLoading';
import '../styles/CardActivationExperience.css';

/** Public intro copy — single source for layout measurement and line-by-line reveal (must match rendered text). */
const DISPLAY_CARD_PUBLIC_INTRO_COPY =
  'This contact uses Velox to always share their current details. Save a live contact on Velox or add a one-time copy to your phone contacts.';

/** Card smoothFadeIn ends ~1.1s; first line starts after. */
const PUBLIC_LINE_ANIM_BASE_S = 1.18;
/** Per-line “typing” reveal length (must match CSS --display-card-line-dur default). */
const PUBLIC_LINE_TYPE_DURATION_S = 1.08;
/** Pause after each line finishes before the next begins. */
const PUBLIC_LINE_INTERLINE_PAUSE_S = 0.16;
const PUBLIC_POST_LINES_PAUSE_S = 0.48;
const PUBLIC_CTA_STAGGER_S = 0.28;

const DisplayCardPage = () => {
  const { nfcUID: nfcParam, userId: digitalUserId } = useParams();
  const location = useLocation();
  const isDigitalProfile = location.pathname.startsWith('/c/');
  const nfcUID = isDigitalProfile ? digitalUserId : nfcParam;
  //const { nfcUID, nfctoken } = useParams();  // Changed token to nfctoken here //For limited time access card tap secuirty 15.12.24
  const [cardInfo, setCardInfo] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [nfcTapInfo, setNfcTapInfo] = useState(null);
  const navigate = useNavigate();
  const [error, setError] = useState('');
  // An NFC tap usually resolves in well under a second, which is exactly the
  // window where a spinner only flickers. Declared after `error` so it reads
  // the live value rather than tripping its temporal dead zone.
  const showCardLoading = useDeferredLoading(!cardInfo && !error, 300);
  const [isNavigating, setIsNavigating] = useState(false);
  const { showSuccess, showError, showInfo } = useToast();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasAutoSaved, setHasAutoSaved] = useState(false);
  const [shareLocationEnabled, setShareLocationEnabled] = useState(true); // Default to true
  const [isAssignableCard, setIsAssignableCard] = useState(false);
  const [assignableType, setAssignableType] = useState(null);
  const [isActivating, setIsActivating] = useState(false);
  const activationInFlightRef = useRef(false);
  const [userHasCard, setUserHasCard] = useState(false);
  const isCheckingUserCard = false;
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [guestPhase, setGuestPhase] = useState('form');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestSubmitting, setGuestSubmitting] = useState(false);
  const [guestFormError, setGuestFormError] = useState('');
  const [guestSavedName, setGuestSavedName] = useState('');
  const [guestVaultDays, setGuestVaultDays] = useState(7);
  /** True when save was queued offline / network-failed — different success copy in modal. */
  const [guestQueuedLocally, setGuestQueuedLocally] = useState(false);
  const nfcViewTrackedRef = useRef(false);
  const publicIntroRef = useRef(null);
  const publicSubRef = useRef(null);
  const [publicSubLines, setPublicSubLines] = useState(null);

  const isPreviewMode = location.state?.fromMyCard;
  const showBackButton = isPreviewMode || isOwner;
  const showPublicCtas =
    Boolean(cardInfo) &&
    !localStorage.getItem('token') &&
    !isOwner &&
    !isPreviewMode &&
    !isAssignableCard;

  useLayoutEffect(() => {
    if (!showPublicCtas) {
      setPublicSubLines(null);
      return;
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setPublicSubLines((prev) =>
        prev?.length === 1 && prev[0] === DISPLAY_CARD_PUBLIC_INTRO_COPY ? prev : [DISPLAY_CARD_PUBLIC_INTRO_COPY]
      );
      return;
    }
    if (publicSubLines !== null) return;

    const root = publicSubRef.current;
    if (!root) return;
    const wordSpans = root.querySelectorAll('.display-card-public-measure-word');
    if (!wordSpans.length) return;

    const linesOut = [];
    let buf = [];
    let lineTop = null;
    wordSpans.forEach((span) => {
      const top = span.offsetTop;
      if (lineTop === null) lineTop = top;
      if (top !== lineTop) {
        linesOut.push(buf.join(' '));
        buf = [];
        lineTop = top;
      }
      buf.push(span.textContent || '');
    });
    if (buf.length) linesOut.push(buf.join(' '));
    setPublicSubLines(linesOut);
  }, [showPublicCtas, publicSubLines]);

  useEffect(() => {
    if (!showPublicCtas || typeof ResizeObserver === 'undefined') return undefined;
    const el = publicIntroRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      setPublicSubLines(null);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [showPublicCtas]);

  useEffect(() => {
    if (!cardInfo || isAssignableCard || nfcViewTrackedRef.current) return;
    nfcViewTrackedRef.current = true;
    if (isDigitalProfile) trackPublicDigitalCardViewed(nfcUID);
    else trackPublicNfcCardViewed(nfcUID);
  }, [cardInfo, isAssignableCard, isDigitalProfile, nfcUID]);

  // Check for pending activation after component mounts (e.g., after login)
  useEffect(() => {
    const checkPostAuthActivation = async () => {
      const token = localStorage.getItem('token');
      const activationCardId = localStorage.getItem('activationCardId');
      const shouldActivate = location.state?.activateCard === true;
      
      // Only auto-activate if this is an assignable card and user just logged in
      if (token && activationCardId && shouldActivate && activationCardId === nfcUID && isAssignableCard) {
        handleActivateCard();
      }
    };

    // Only run this check if we have an assignable card
    if (isAssignableCard) {
      checkPostAuthActivation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfcUID, location.state, isAssignableCard]);

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        // Detect if this was accessed via NFC tap vs direct browser navigation
        const nfcDetection = detectNfcAccess();
        
        // Simple API call - no token complexity needed
        const response = await api.get(
          isDigitalProfile
            ? `/api/c/${encodeURIComponent(nfcUID)}`
            : `/api/nfc/${encodeURIComponent(nfcUID)}`
        );

        // Check if this is an assignable in-person card
        if (response.data.assignable === true && response.data.assigned === false) {
          setIsAssignableCard(true);
          setAssignableType(response.data.type || response.data.channel || null);
          const hint = response.data.reservedEmailHint
            ? ` Purchase email ${response.data.reservedEmailHint}.`
            : '';
          setCardInfo({
            cardId: response.data.cardId || nfcUID,
            message: `${response.data.activationMessage
              || response.data.message
              || 'This Velox card is ready to be activated and linked to your account.'}${hint}`
          });
          return;
        }

        // Normal card flow
        const cardPayload = response.data.cardInfo ?? response.data;
        setCardInfo(cardPayload);
        setIsAssignableCard(false);
        
        // Get the logged-in user's information and privacy settings (only if token exists)
        // Location can only be saved if BOTH users have location sharing enabled
        let locationSharingEnabled = true; // Default to enabled
        const cardOwnerLocationEnabled = cardPayload.locationSharingEnabled !== false; // Card owner's preference
        
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const userResponse = await api.get('/api/user/account');
            
            // Check if the logged-in user is the owner of the card being viewed
            const loggedInUserId = userResponse.data._id;
            setIsOwner(cardPayload.userId === loggedInUserId);
            
            // Fetch privacy settings to check location sharing preference for current user
            const privacyResponse = await api.get('/api/user/privacy-settings');
            const currentUserLocationEnabled = privacyResponse.data.shareLocationOnContact !== false;
            
            // Both users must have location sharing enabled
            locationSharingEnabled = currentUserLocationEnabled && cardOwnerLocationEnabled;
            setShareLocationEnabled(locationSharingEnabled);
          } catch (error) {
            console.error('Error fetching user data:', error);
            // Default to enabled if we can't fetch settings, but still check card owner's preference
            locationSharingEnabled = cardOwnerLocationEnabled;
            setShareLocationEnabled(locationSharingEnabled);
          }
        } else {
          // If not logged in, we can't save contacts anyway, but still check card owner's preference
          locationSharingEnabled = cardOwnerLocationEnabled;
        }
        
        // Use only client-side NFC detection for location restrictions
        // Also check both users' privacy settings - both must allow location sharing
        setNfcTapInfo({
          isLikelyNfc: nfcDetection.isLikelyNfc,
          confidence: nfcDetection.confidence,
          reasons: nfcDetection.reasons,
          locationSaveAllowed: nfcDetection.isLikelyNfc && isWithin15Minutes() && locationSharingEnabled
        });
      } catch (error) {
        console.error('Error fetching card info:', error);
        if (error.response?.status === 404) {
          setError('Card not found.');
        } else {
          setError('Failed to fetch card information.');
        }
      }
    };

    fetchCardData();
  }, [isDigitalProfile, nfcUID]);

  // Simple function to check if we're within 15 minutes of page load
  const isWithin15Minutes = () => {
    const pageLoadTime = sessionStorage.getItem('cardPageLoadTime');
    if (!pageLoadTime) return true; // Fresh page load
    
    const timeSinceLoad = Date.now() - parseInt(pageLoadTime);
    return timeSinceLoad <= 15 * 60 * 1000; // 15 minutes
  };

  // Function to detect if page was accessed via NFC tap
  const detectNfcAccess = () => {
    const detection = {
      isLikelyNfc: false,
      confidence: 0,
      reasons: []
    };

    // Check 1: No referrer (NFC taps typically don't have referrer)
    const referrer = document.referrer;
    if (!referrer) {
      // Truly no referrer - likely NFC tap
      detection.confidence += 40;
      detection.reasons.push('No referrer (direct access)');
    } else if (referrer && !referrer.includes(window.location.hostname)) {
      // External referrer - likely shared link, reduce confidence
      detection.confidence -= 10;
      detection.reasons.push('External referrer (shared link)');
    }

    // Check 2: Direct navigation (not from another page on site)
    const navigationEntries = performance.getEntriesByType('navigation');
    if (navigationEntries.length > 0) {
      const navEntry = navigationEntries[0];
      if (navEntry.type === 'navigate' && !referrer) {
        detection.confidence += 30;
        detection.reasons.push('Direct navigation without referrer');
      }
    }

    // Check 3: Page load time patterns (NFC typically faster initial load)
    const pageLoadTime = sessionStorage.getItem('cardPageLoadTime');
    if (!pageLoadTime) {
      sessionStorage.setItem('cardPageLoadTime', Date.now().toString());
      detection.confidence += 20;
      detection.reasons.push('Fresh page access');
    }

    // Check 4: User agent patterns (mobile devices more likely for NFC)
    const userAgent = navigator.userAgent;
    if (/Mobile|Android|iPhone|iPad/.test(userAgent)) {
      detection.confidence += 10;
      detection.reasons.push('Mobile device');
    }

    // Check 5: No session storage from previous site navigation
    const hasSessionData = sessionStorage.getItem('velox_navigation_session');
    if (!hasSessionData) {
      sessionStorage.setItem('velox_navigation_session', 'true');
      detection.confidence += 15;
      detection.reasons.push('No previous session data');
    }

    detection.isLikelyNfc = detection.confidence >= 60; // Threshold for NFC detection

    console.log('🔍 NFC Detection:', detection);
    return detection;
  };

  // Add this useEffect to monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Process any queued contacts when coming back online
      processQueuedContacts();
      window.setTimeout(() => {
        flushGuestSaveQueue();
      }, 250);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  useEffect(() => {
    flushGuestSaveQueue();
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        flushGuestSaveQueue();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    const onFocus = () => {
      flushGuestSaveQueue();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Function to process queued contacts when coming back online
  const processQueuedContacts = async () => {
    const queuedContacts = JSON.parse(localStorage.getItem('queuedContacts') || '[]');
    
    if (queuedContacts.length > 0) {
      showInfo(`Processing ${queuedContacts.length} queued contact(s)...`);
      
      let successCount = 0;
      let errorCount = 0;
      
      for (const contactData of queuedContacts) {
        try {
          const token = localStorage.getItem('token');
          if (token) {
            // Remove headers from contactData (it was stored with old token)
            const { headers, ...payload } = contactData;
            await api.post('/api/contact/create', payload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          console.error('Error processing queued contact:', error);
          // If it's a duplicate, that's fine - count as success
          if (error.response && error.response.status === 400 && 
              typeof error.response.data?.error === 'string' &&
              error.response.data.error.includes('Contact already exists')) {
            successCount++;
          } else {
            errorCount++;
          }
        }
      }
      
      // Clear the queue after processing
      localStorage.removeItem('queuedContacts');
      
      // Show summary
      if (successCount > 0) {
        showSuccess(`${successCount} queued contact${successCount > 1 ? 's' : ''} saved successfully!`);
      }
      if (errorCount > 0) {
        showError(`Failed to save ${errorCount} queued contact${errorCount > 1 ? 's' : ''}.`);
      }
    }
  };

  // Auto-save after delay for logged-in viewers only (anonymous users use explicit CTAs)
  useEffect(() => {
    const isPreviewMode = location.state?.fromMyCard;
    const token = localStorage.getItem('token');

    if (
      cardInfo &&
      token &&
      !isOwner &&
      !hasAutoSaved &&
      !isPreviewMode &&
      !isAssignableCard
    ) {
      const autoSaveTimer = setTimeout(() => {
        setHasAutoSaved(true);
        handleCreateContact();
      }, 4000);

      return () => clearTimeout(autoSaveTimer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardInfo, isOwner, hasAutoSaved, location.state, isAssignableCard]);

  const openGuestSaveModal = () => {
    trackGuestSaveModalOpen(nfcUID);
    setGuestModalOpen(true);
    setGuestPhase('form');
    setGuestEmail('');
    setGuestFormError('');
    setGuestSavedName('');
    setGuestVaultDays(7);
    setGuestQueuedLocally(false);
  };

  const closeGuestSaveModal = () => {
    if (guestSubmitting) return;
    setGuestModalOpen(false);
  };

  const handleSaveToVelox = () => {
    openGuestSaveModal();
  };

  const submitGuestEmail = async (e) => {
    e.preventDefault();
    setGuestFormError('');
    const trimmed = guestEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setGuestFormError('Please enter a valid email address.');
      return;
    }
    setGuestSubmitting(true);
    setGuestQueuedLocally(false);

    const placeholderName = 'Name not available';
    const localDisplayName =
      cardInfo?.name && cardInfo.name !== placeholderName ? cardInfo.name.trim() : '';
    const applyLocalQueuedSuccess = () => {
      enqueueGuestSave({ email: trimmed, nfcUID });
      setGuestSavedName(localDisplayName || 'Contact');
      setGuestVaultDays(7);
      setGuestQueuedLocally(true);
      setGuestPhase('done');
    };

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      trackGuestEmailSubmitted(nfcUID);
      applyLocalQueuedSuccess();
      setGuestSubmitting(false);
      return;
    }

    try {
      trackGuestEmailSubmitted(nfcUID);
      const res = await guestApi.post('/api/guest/request-link', { email: trimmed, nfcUID });
      if (res.data?.ok) {
        trackGuestImmediateSaveSuccess(nfcUID);
        if (res.data.emailSent) {
          trackGuestFollowupEmailSent(nfcUID);
        }
        setGuestSavedName(res.data.savedName || 'Contact');
        setGuestVaultDays(
          typeof res.data.sessionExpiresInDays === 'number' ? res.data.sessionExpiresInDays : 7
        );
        setGuestPhase('done');
      } else {
        setGuestFormError('Something went wrong. Please try again.');
      }
    } catch (err) {
      if (shouldEnqueueGuestSaveFromRequestError(err)) {
        applyLocalQueuedSuccess();
      } else {
        const msg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          (err.response?.status === 404
            ? 'This card could not be found.'
            : 'Something went wrong. Please try again.');
        setGuestFormError(typeof msg === 'string' ? msg : 'Please try again.');
      }
    } finally {
      setGuestSubmitting(false);
    }
  };

  const handleSignIn = () => {
    navigate('/login', {
      state: { returnUrl: window.location.pathname },
    });
  };

  const handleAddToContacts = async () => {
    if (!cardInfo) return;
    trackAddToContactsClick(nfcUID);
    const placeholder = 'Name not available';
    const name = cardInfo.name && cardInfo.name !== placeholder ? cardInfo.name.trim() : '';
    if (!name) {
      showError('This card does not include a name, so it cannot be added to Contacts.');
      return;
    }

    const contactPayload = {
      name,
      email: cardInfo.email,
      phone: cardInfo.phone,
      company: cardInfo.company,
      role: cardInfo.role,
      title: cardInfo.title,
      linkedinURL: cardInfo.linkedinURL,
      additionalURL: cardInfo.additionalURL,
    };

    const result = await shareOrDownloadVCard(
      contactPayload,
      { showSuccess, showError },
      { trackId: null, trackAction: false }
    );
    if (result.ok) {
      trackAddToContactsSuccess(nfcUID);
    }
  };

  const handleCreateContact = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    // Check if this is a delayed save (more than 5 minutes since page load)
    const pageLoadTime = sessionStorage.getItem('cardPageLoadTime') || Date.now();
    const timeSinceLoad = Date.now() - parseInt(pageLoadTime);
    const isDelayedSave = timeSinceLoad > 300000; // 5 minutes

    // Store page load time for delayed save detection
    if (!sessionStorage.getItem('cardPageLoadTime')) {
      sessionStorage.setItem('cardPageLoadTime', Date.now().toString());
    }

    const saveContact = async (latitude, longitude, useCurrentLocation = true) => {
      const payload = { 
        nfcUID,
        name: cardInfo.name,
        email: cardInfo.email,
        phone: cardInfo.phone,
        company: cardInfo.company,
        role: cardInfo.role,
        linkedinURL: cardInfo.linkedinURL,
        additionalURL: cardInfo.additionalURL,
        title: cardInfo.title,
        ...(latitude && longitude && useCurrentLocation && { lat: latitude, lng: longitude })
      };

      try {
        // Check if we're offline
        if (!isOnline) {
          // Queue the contact for later processing
          const queuedContacts = JSON.parse(localStorage.getItem('queuedContacts') || '[]');
          queuedContacts.push({
            ...payload,
            queuedAt: Date.now(),
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('queuedContacts', JSON.stringify(queuedContacts));
          
          showSuccess('📱 Connection lost: Contact queued and will be saved automatically when connection is restored!');
          
          // Clear session data
          localStorage.removeItem('pendingCardSave');
          sessionStorage.removeItem('cardPageLoadTime');
          
          navigate('/contacts');
          return;
        }

        const response = await api.post('/api/contact/create', 
          payload, 
          { headers: { Authorization: `Bearer ${token}` } } 
        );

        if (response.status === 201) {
          // Clear any pending card save
          localStorage.removeItem('pendingCardSave');
          sessionStorage.removeItem('cardPageLoadTime');
          
          if (latitude && longitude && useCurrentLocation) {
            showSuccess('Contact saved successfully with meeting location!');
          } else {
            showSuccess('Contact saved successfully!');
          }
          navigate('/contacts');
        }
      } catch (error) {
        // If it's a network error and we weren't already handling offline, queue it
        if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
          const queuedContacts = JSON.parse(localStorage.getItem('queuedContacts') || '[]');
          queuedContacts.push({
            ...payload,
            queuedAt: Date.now(),
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem('queuedContacts', JSON.stringify(queuedContacts));
          
          showInfo('📱 Network error: Contact queued and will be saved when connection is restored!');
          navigate('/contacts');
          return;
        }

        if (error.response && error.response.status === 400 &&
            typeof error.response.data?.error === 'string' &&
            error.response.data.error.includes('Contact already exists')) {
          showInfo('This contact is already saved in your contacts.');
          navigate('/contacts');
        } else {
          console.error('Error saving contact:', error.response?.data || error.message);
          
          // Handle network errors (different from line 385 - catches other network error formats)
          if (!error.response) {
            showError('Unable to connect to the server. Please check your internet connection and try again.');
            return;
          }
          
          // Backend returns user-friendly messages, use them directly
          const errorMessage = error.response?.data?.error || '';
          const statusCode = error.response?.status;
          
          if (statusCode === 500) {
            showError('An unexpected error occurred. Please try again in a moment.');
          } else if (errorMessage) {
            // Backend messages are already user-friendly
            showError(errorMessage);
          } else {
            showError('Unable to save contact. Please try again.');
          }
        }
      }
    };

    // Handle delayed save scenario
    if (isDelayedSave) {
      const userConfirmed = window.confirm(
        `It's been ${Math.round(timeSinceLoad / 60000)} minutes since you opened this card. ` +
        `Do you want to save your current location as where you met, or save without location?\n\n` +
        `Click OK to use current location, or Cancel to save without location.`
      );
      
      if (!userConfirmed) {
        // User chose not to use current location
        showInfo('Contact saved without location.');
        saveContact(null, null, false);
        return;
      }
    }

    // Simple location capture with NFC detection
    if (navigator.geolocation) {
      // Check if location saving is allowed (NFC detected, within 15 minutes, and both users' preferences enabled)
      if (!nfcTapInfo?.locationSaveAllowed || !shareLocationEnabled) {
        saveContact(); // Save without location
        return;
      }
      
      // Show user feedback that we're getting location
      if (isDelayedSave) {
        showInfo('Getting your current location...');
      } else {
        showInfo('Getting your location...');
      }
      
      const options = {
        enableHighAccuracy: true,  // Request high accuracy
        timeout: 10000,           // 10 second timeout
        maximumAge: 300000        // Accept cached position up to 5 minutes old
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          
          // Check location accuracy and inform user
          if (accuracy > 100) { // More than 100 meters accuracy
            showInfo(`Meeting location detected (±${Math.round(accuracy)}m accuracy).`);
          } else {
            showSuccess(`Precise meeting location captured (±${Math.round(accuracy)}m accuracy)`);
          }
          
          saveContact(latitude, longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
          
          // Provide specific error messages and always save contact
          switch(error.code) {
            case error.PERMISSION_DENIED:
              showInfo('Location access denied. Contact saved without location.');
              break;
            case error.POSITION_UNAVAILABLE:
              showInfo('Location unavailable (poor GPS signal). Contact saved without location.');
              break;
            case error.TIMEOUT:
              showInfo('Location request timed out. Contact saved without location.');
              break;
            default:
              showInfo('Unable to get location. Contact saved without location.');
              break;
          }
          
          // ALWAYS save the contact even if location fails
          saveContact();
        },
        options
      );
    } else {
      showInfo('Location services not available. Contact saved without location.');
      saveContact(); // Save contact without location if geolocation is not supported
    }
  };

  const handleBackClick = () => {
    setIsNavigating(true);
    navigate('/my-card');
  };

  const handleActivateCard = async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      localStorage.setItem('activationCardId', nfcUID);
      const proof = new URLSearchParams(location.search).get('proof');
      const returnUrl = proof
        ? `/nfc/${encodeURIComponent(nfcUID)}?proof=${encodeURIComponent(proof)}`
        : `/nfc/${nfcUID}`;
      navigate('/login', { state: { returnUrl, activateCard: true } });
      return;
    }

    if (activationInFlightRef.current) return;
    activationInFlightRef.current = true;
    setIsActivating(true);
    setError('');

    try {
      const proof = new URLSearchParams(location.search).get('proof');
      const response = await activateCard(nfcUID, proof);

      if (response.status === 200) {
        showSuccess('Card successfully activated and linked to your account!');
        // Navigate to My Card page after a brief delay
        setTimeout(() => {
          navigate('/my-card', { state: { cardActivated: true } });
        }, 1500);
      }
    } catch (error) {
      console.error('Error activating card:', error);
      const errorMessage = activationErrorMessage(error);
      setError(errorMessage);
      showError(errorMessage);

      // Only a card already linked to *this* account means the user has a card;
      // a card claimed by someone else, or reserved for another address, does not.
      if (isAlreadyHasCardError(error)) {
        setUserHasCard(true);
      }
    } finally {
      setIsActivating(false);
      activationInFlightRef.current = false;
    }
  };

  const isLoggedIn = Boolean(localStorage.getItem('token'));

  if (!cardInfo && error) {
    return (
      <div className="velox-activation-page">
        <div className="velox-activation-page__inner">
          <div className="velox-activation-not-found">
            <h1 className="velox-activation-not-found__title">Card not found</h1>
            <p className="velox-activation-not-found__text">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!cardInfo) {
    // The page canvas is already near-black, so a short wait shows a calm empty
    // screen rather than a spinner that appears and leaves in the same breath.
    return (
      <div className="velox-activation-page">
        <div className="velox-activation-page__inner">
          {showCardLoading ? (
            <div className="velox-activation-loading">
              <div className="velox-activation-loading__spinner" aria-hidden="true" />
              <p className="velox-activation-loading__title">One moment</p>
              <p className="velox-activation-loading__subtitle">Recognising your card.</p>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (isAssignableCard) {
    return (
      <div className="velox-activation-page">
        <div className="velox-activation-page__inner">
          <CardActivationPanel
            channel={assignableType}
            isLoggedIn={isLoggedIn}
            isActivating={isActivating}
            isChecking={isCheckingUserCard}
            userHasCard={userHasCard}
            error={error}
            onActivate={handleActivateCard}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        showPublicCtas
          ? 'display-card-page display-card-page--staged-public-cta'
          : 'display-card-page'
      }
    >
      <VeloxCard
        card={cardInfo}
        variant="presentation"
        links="interactive"
        className="display-card-surface"
      />

      {showPublicCtas && (
        <div className="display-card-public-actions" aria-label="Save this contact">
          {/*
            Explanatory intro copy temporarily removed from the rendered UI.
            Kept here (restorable) along with its typed-line reveal animation. To bring
            the text back, uncomment the block below and revert the CTA wrapper's
            className/style to depend on `publicSubLines` (pending-measure gating).

            Copy:
            "This contact uses Velox to always share their current details. Save a live
            contact on Velox or add a one-time copy to your phone contacts."

            <div className="display-card-public-intro" ref={publicIntroRef}>
              <p ref={publicSubRef} className="display-card-public-sub">
                {publicSubLines === null
                  ? DISPLAY_CARD_PUBLIC_INTRO_COPY.split(/\s+/).filter(Boolean).map((word, i) => (
                      <React.Fragment key={i}>
                        {i > 0 ? ' ' : null}
                        <span className="display-card-public-measure-word">{word}</span>
                      </React.Fragment>
                    ))
                  : publicSubLines.map((line, i) => (
                      <span key={i} className="display-card-public-line">
                        <span
                          className="display-card-public-line-type"
                          style={{
                            '--display-card-line-delay': `${
                              PUBLIC_LINE_ANIM_BASE_S + i * (PUBLIC_LINE_TYPE_DURATION_S + PUBLIC_LINE_INTERLINE_PAUSE_S)
                            }s`,
                            '--display-card-line-dur': `${PUBLIC_LINE_TYPE_DURATION_S}s`,
                          }}
                        >
                          {line}
                        </span>
                      </span>
                    ))}
              </p>
            </div>
          */}
          <div
            className="display-card-public-ctas"
            style={(() => {
              // Intro copy is removed, so there are no typed lines to measure. Start the
              // CTA stagger shortly after the card finishes animating in. Delay vars are
              // mapped to the visual order so the TOP button (Save to Contacts → delay-2)
              // reveals first, then the primary (delay-1), then the sign-in link (delay-3).
              const n = publicSubLines && publicSubLines.length > 0 ? publicSubLines.length : 0;
              const linePhaseEnd =
                PUBLIC_LINE_ANIM_BASE_S +
                n * PUBLIC_LINE_TYPE_DURATION_S +
                Math.max(0, n - 1) * PUBLIC_LINE_INTERLINE_PAUSE_S;
              // No intro lines to wait for, so reveal the CTAs early (overlapping the
              // card's fade-in) instead of waiting for the full line phase.
              const t0 = n > 0 ? linePhaseEnd + PUBLIC_POST_LINES_PAUSE_S : 1;
              return {
                '--display-card-cta-delay-2': `${t0}s`,
                '--display-card-cta-delay-1': `${t0 + PUBLIC_CTA_STAGGER_S}s`,
                '--display-card-cta-delay-3': `${t0 + 2 * PUBLIC_CTA_STAGGER_S}s`,
              };
            })()}
          >
            <button type="button" className="display-card-action-secondary" onClick={handleAddToContacts}>
              Save to Contacts
            </button>
            <button type="button" className="display-card-action-primary" onClick={handleSignIn}>
              Save to Velox App
            </button>
            <button type="button" className="display-card-action-signin" onClick={handleSaveToVelox}>
              Don’t have an account? Save and sign up later.
            </button>
          </div>
        </div>
      )}

      {showBackButton && !isNavigating && (
        <button type="button" className="back-to-contacts-button" onClick={handleBackClick}>
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      {error && <p className="error">{error}</p>}

      {guestModalOpen && (
        <div
          className="display-card-guest-overlay"
          role="presentation"
          onClick={closeGuestSaveModal}
        >
          <div
            className="display-card-guest-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guest-save-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <button
              type="button"
              className="display-card-guest-close"
              onClick={closeGuestSaveModal}
              aria-label="Close"
            >
              ×
            </button>
            {guestPhase === 'form' && (
              <>
                <h2 id="guest-save-title" className="display-card-guest-title">
                  Save live contact on Velox
                </h2>
                <p className="display-card-guest-copy">
                  We’ll email you a secure link to sign up later and easily add this contact to your account.
                </p>
                <p className="display-card-guest-copy display-card-guest-copy-tight">
                  Saving this contact to a Velox account will mean you can always see their latest details.
                </p>
                <form onSubmit={submitGuestEmail} className="display-card-guest-form">
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    inputMode="email"
                    placeholder="Your email"
                    value={guestEmail}
                    onChange={(ev) => setGuestEmail(ev.target.value)}
                    className="display-card-guest-input"
                    disabled={guestSubmitting}
                    aria-label="Email address"
                  />
                  {guestFormError && (
                    <p className="display-card-guest-error" role="alert">
                      {guestFormError}
                    </p>
                  )}
                  {/* New tab: this page is the NFC tap target, and navigating
                      away in-place would lose the card the guest is saving. */}
                  <p className="display-card-guest-trust">
                    We only use your email to send the save link.{' '}
                    <a href="/policy/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </p>
                  <button
                    type="submit"
                    className="display-card-guest-submit"
                    disabled={guestSubmitting}
                  >
                    {guestSubmitting ? 'Saving…' : 'Share link'}
                  </button>
                </form>
              </>
            )}
            {guestPhase === 'done' && (
              <>
                <h2 id="guest-save-title" className="display-card-guest-title">
                  Almost there!
                </h2>
                {guestQueuedLocally ? (
                  <>
                    <p className="display-card-guest-copy">
                      Saved locally — we’ll finish this when you’re back online.
                    </p>
                    <p className="display-card-guest-copy display-card-guest-copy-follow">
                      We’ll email you a secure link for <strong>{guestSavedName}'s</strong> live contact at{' '}
                      <strong>{guestEmail.trim()}</strong> once you’re connected. Sign in with that email within{' '}
                      <strong>{guestVaultDays} days</strong> if you’d like to add them to your Velox account.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="display-card-guest-copy">
                      We’ve sent you a secure link to access a live version of <strong>{guestSavedName}'s</strong>{' '}
                      contact details.
                    </p>
                    <p className="display-card-guest-copy display-card-guest-copy-follow">
                      Create an account for free and sign in with your email <strong>{guestEmail.trim()}</strong> within{' '}
                      <strong>{guestVaultDays} days</strong> if you’d like to add this contact to your Velox account.
                    </p>
                  </>
                )}
                <button
                  type="button"
                  className="display-card-guest-submit"
                  onClick={closeGuestSaveModal}
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayCardPage;
